import { useState, useRef, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { routes } from '../config';
import Home from '../pages/Home';
import Login from '../pages/Login';
import OTP from '../pages/OTP';
import Register from '../pages/Register';
import ReferralCode from '../pages/ReferralCode';
import RegisterSuccess from '../pages/RegisterSuccess';
import CouponDetail from '../pages/CouponDetail';
import CouponsPage from '../pages/CouponsPage';
import StampListPage from '../pages/StampListPage';
import RedeemPage from '../pages/RedeemPage';
import NewsPage from '../pages/NewsPage';
import AccountPage from '../pages/AccountPage';
import TransactionHistoryPage from '../pages/TransactionHistoryPage';
import WalletPage from '../pages/WalletPage';
import ReceiptSubmissionsPage from '../pages/ReceiptSubmissionsPage';
import MemberPrivilegesPage from '../pages/MemberPrivilegesPage';
import AuthGuard from '../components/AuthGuard';
import BottomTabBar from '../components/BottomTabBar';
import QrModal from '../components/QrModal';
import type { AppRoutesProps, Customer, FirebaseUser } from '../types';

interface ExtendedAppRoutesProps extends Omit<AppRoutesProps, 'currentUser'> {
  currentUser: Customer | null;
  firebaseUser: FirebaseUser | null;
}

const TAB_BAR_ROUTES = new Set<string>([routes.home, routes.coupons, routes.stampList, routes.redeem, routes.couponDetail, routes.news, routes.account, routes.transactionHistory, routes.wallet, routes.receiptSubmissions, routes.memberPrivileges]);

function AppRoutesContent({
  currentUser,
  registerFormData,
  firebaseUser,
  onBack,
  onBackToLogin,
}: ExtendedAppRoutesProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [qrOpen, setQrOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const showTabBar = TAB_BAR_ROUTES.has(location.pathname);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [location.pathname]);

  const routes_el = (
    <Routes>
      <Route path={routes.login} element={<Login />} />
      <Route path={routes.otp} element={<OTP />} />
      <Route
        path={routes.register}
        element={<Register firebaseUser={firebaseUser} onBack={onBackToLogin} initialFormData={registerFormData} />}
      />
      <Route
        path={routes.registerSuccess}
        element={<RegisterSuccess onContinue={() => navigate(routes.home)} />}
      />
      <Route
        path={routes.referralCode}
        element={
          <AuthGuard currentUser={currentUser} firebaseUser={firebaseUser}>
            <ReferralCode onBack={onBack} />
          </AuthGuard>
        }
      />
      <Route
        path={routes.couponDetail}
        element={
          <AuthGuard currentUser={currentUser} firebaseUser={firebaseUser}>
            <CouponDetail />
          </AuthGuard>
        }
      />
      <Route
        path={routes.coupons}
        element={
          <AuthGuard currentUser={currentUser} firebaseUser={firebaseUser}>
            <CouponsPage user={currentUser} />
          </AuthGuard>
        }
      />
      <Route
        path={routes.stampList}
        element={
          <AuthGuard currentUser={currentUser} firebaseUser={firebaseUser}>
            <StampListPage />
          </AuthGuard>
        }
      />
      <Route
        path={routes.redeem}
        element={
          <AuthGuard currentUser={currentUser} firebaseUser={firebaseUser}>
            <RedeemPage />
          </AuthGuard>
        }
      />
      <Route
        path={routes.home}
        element={
          <AuthGuard currentUser={currentUser} firebaseUser={firebaseUser}>
            <Home user={currentUser!} onQrClick={() => setQrOpen(true)} />
          </AuthGuard>
        }
      />
      <Route
        path={routes.news}
        element={
          <AuthGuard currentUser={currentUser} firebaseUser={firebaseUser}>
            <NewsPage />
          </AuthGuard>
        }
      />
      <Route
        path={routes.account}
        element={
          <AuthGuard currentUser={currentUser} firebaseUser={firebaseUser}>
            <AccountPage />
          </AuthGuard>
        }
      />
      <Route
        path={routes.transactionHistory}
        element={
          <AuthGuard currentUser={currentUser} firebaseUser={firebaseUser}>
            <TransactionHistoryPage />
          </AuthGuard>
        }
      />
      <Route
        path={routes.wallet}
        element={
          <AuthGuard currentUser={currentUser} firebaseUser={firebaseUser}>
            <WalletPage />
          </AuthGuard>
        }
      />
      <Route
        path={routes.receiptSubmissions}
        element={
          <AuthGuard currentUser={currentUser} firebaseUser={firebaseUser}>
            <ReceiptSubmissionsPage />
          </AuthGuard>
        }
      />
      <Route
        path={routes.memberPrivileges}
        element={
          <AuthGuard currentUser={currentUser} firebaseUser={firebaseUser}>
            <MemberPrivilegesPage />
          </AuthGuard>
        }
      />
      <Route path="*" element={<Navigate to={routes.home} replace />} />
    </Routes>
  );

  const qrModal = qrOpen ? <QrModal user={currentUser} onClose={() => setQrOpen(false)} /> : null;

  if (isDesktop) {
    return (
      <Box sx={{
        minHeight: '100svh',
        backgroundColor: '#1A1A1A',
        display: 'flex', justifyContent: 'center',
      }}>
        <Box sx={{
          width: '390px',
          minHeight: '100svh',
          backgroundColor: '#fff',
          display: 'flex', flexDirection: 'column',
          position: 'relative',
        }}>
          <Box ref={scrollRef} sx={{
            position: 'absolute', inset: 0,
            overflowY: 'auto', overflowX: 'hidden',
            display: 'flex', flexDirection: 'column',
            pb: showTabBar ? '64px' : 0,
            '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none',
          }}>
            {routes_el}
          </Box>
          {showTabBar && (
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 30 }}>
              <BottomTabBar onQrClick={() => setQrOpen(true)} />
            </Box>
          )}
          {qrModal}
        </Box>
      </Box>
    );
  }

  // Mobile: full-screen with nav bar fixed at bottom
  return (
    <Box sx={{ position: 'fixed', inset: 0, backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
      <Box ref={scrollRef} sx={{
        position: 'absolute', inset: 0,
        overflowY: 'auto', overflowX: 'hidden',
        display: 'flex', flexDirection: 'column',
        pb: showTabBar ? '64px' : 0,
        '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none',
      }}>
        {routes_el}
      </Box>
      {showTabBar && (
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 30 }}>
          <BottomTabBar onQrClick={() => setQrOpen(true)} />
        </Box>
      )}
      {qrModal}
    </Box>
  );
}

export default function AppRoutes(props: ExtendedAppRoutesProps) {
  return <AppRoutesContent {...props} />;
}
